import {
  InterfacesInitializerStatePropsType,
  UserContextEnum
} from '../../types';
import { RefreshType, AccessControlOptionsType } from './types';
import Store from '../store/index';
import EventNames from '../../../config/eventNames';
import { EventsInterfaceType } from '@clientos/types';
import { Scope } from '../../../services/scope';
import { PromiseHelper } from '../../../utils/PromiseHelper';
import BaseContainer from '../../../containers/BaseContainer';
import { IScopeService } from '../../../services/scope/IScopeService';
import { AccessControlInputType } from '../../../infra/commonInitializer/types';
import { IActionService } from '../../../services/action/types';
import userContextEnumToAuthContextEnum from '../../../services/authTokenService/utils/userContextEnumToAuthContextEnum';
import IApplicationService from '../../../services/applicationService/IApplicationService';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';

export default async ({
  accessControl,
  store,
  events,
  applicationService,
  scopeService,
  actionService,
  accessControlContainer
}: InterfacesInitializerStatePropsType & {
  accessControl: AccessControlInputType;
  store: Store;
  events: EventsInterfaceType;
  applicationService: IApplicationService;
  scopeService: IScopeService;
  actionService: IActionService;
  accessControlContainer: BaseContainer;
}) => {
  const {
    enabled,
    actions: manifestActions,
    actionsPrefix
  } = accessControl || {};
  const defaultKey = 'default';
  const initializedUserContexts: Map<
    UserContextEnum | typeof defaultKey,
    boolean
  > = new Map();
  const promiseHelper = new PromiseHelper();

  async function refreshPromise(options?: AccessControlOptionsType) {
    const policiesChanged = isProviderOneCloud()
      ? await actionService.refresh(
          userContextEnumToAuthContextEnum(options?.userContext)
        )
      : await scopeService.updateScopes(options);

    if (!policiesChanged) return;

    events.triggerEvent(
      EventNames.shellAccessControlChangedEventName,
      undefined
    );
  }

  async function refresh(
    refreshOptions?: RefreshType, // TODO: Why is this not used?
    options?: AccessControlOptionsType
  ) {
    const promiseKey = options?.userContext ?? defaultKey;
    let promise = promiseHelper.getPendingPromise(promiseKey);
    if (isEnabled() && !promise) {
      promise = refreshPromise(options);
      promiseHelper.setPendingPromise(promiseKey, promise);
    }

    if (promise) {
      await promise;
    }
  }

  /**
   * During initialization, if AuthProvider is OneCloud, uses the initial
   * policies (actions) from the manifest.
   * If AuthZ, refreshes all the policies (scopes) from the API.
   */
  async function _initialize(options?: AccessControlOptionsType) {
    const contextKey = options?.userContext ?? defaultKey;
    let promise = promiseHelper.getPendingPromise(contextKey);
    const isInitialized = initializedUserContexts.get(contextKey);

    if (!isInitialized) {
      initializedUserContexts.set(contextKey, true);
      if (!promise) {
        promise = isProviderOneCloud()
          ? actionService.addIfNew(
              userContextEnumToAuthContextEnum(options?.userContext),
              actionService.normalizeActionNames(manifestActions, actionsPrefix)
            )
          : refresh({ reloadContent: false }, options);
        promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    if (promise) {
      await promise;
    }
  }

  /**
   * @deprecated Use `check` instead.
   */
  async function checkScopes(
    scopesToCheck: Scope[],
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!isEnabled()) return true;

    await _initialize(options);
    //TODO: get the info for the user token based on the useContext
    return scopeService.checkScopes(scopesToCheck, options);
  }

  async function check(policies: string[], options: AccessControlOptionsType) {
    if (!isEnabled()) return true;

    await _initialize(options);

    if (isProviderOneCloud()) {
      const authContext = userContextEnumToAuthContextEnum(
        options?.userContext
      );
      return actionService.check(
        authContext,
        actionService.normalizeActionNames(policies, actionsPrefix)
      );
    }

    return scopeService.checkScopes(
      scopeService.convertToScopes(policies),
      options
    );
  }

  function isEnabled() {
    if (!enabled) {
      console.warn('AccessControl: disabled');
      return false;
    }
    return true;
  }

  function isProviderOneCloud(): boolean {
    return (
      applicationService.getAuthenticationProvider() ===
      AuthenticationProviderEnum.coptor
    );
  }

  accessControlContainer.addInitServices(_initialize);
  accessControlContainer.addRefreshServices(refresh);

  return { refresh, checkScopes, check };
};
